class CustomBundleBuilder extends HTMLElement {
  /**
   * Upper bound on an open-ended bundle. Spend thresholds replaced pack sizes,
   * so nothing else caps it — but an unbounded selection means an unbounded
   * slot list and an unbounded cart payload, so there is a ceiling well above
   * any plausible real bundle. A section with a fixed bundle size overrides
   * this with that size (see the maxItems getter).
   */
  static MAX_ITEMS = 24;

  /**
   * Tier discount types whose value is a percentage rather than a money
   * amount. Single source of truth for the parse and the formatters, so a type
   * can never be understood by one and not the other.
   */
  static PERCENT_TYPES = ['percent', 'percent_item'];

  /**
   * Reads one tier button's data attributes into the shape the ladder works
   * in. Static and separate from the DOM walk so the mapping can be exercised
   * directly — an unrecognised discount type here does not throw, it silently
   * pays out cents, which is exactly the kind of failure that needs a test
   * rather than a reviewer.
   *
   * @param {{dataset:object}} button
   * @param {number} index
   * @returns {{index:number, button:*, label:string, threshold:number, discountType:string, discountValue:number}}
   */
  static parseTier(button, index) {
    const { dataset } = button;
    return {
      index,
      button,
      label: dataset.label || '',
      // Cents on a spend ladder, a plain item count on a quantity one.
      threshold: parseInt(dataset.threshold, 10) || 0,
      // Anything unrecognised falls back to a fixed amount, the one reading
      // that cannot silently inflate a saving: a percentage misread as an
      // amount is a few cents, where an amount misread as a percentage would
      // quote a slice of the whole bundle. Every percentage type must appear
      // in PERCENT_TYPES — one that doesn't lands here and pays out cents.
      discountType: CustomBundleBuilder.PERCENT_TYPES.includes(dataset.discountType)
        ? dataset.discountType
        : 'amount',
      // Cents for a fixed amount, a percentage for the rest — resolved against
      // a real selection by CustomBundleBuilder.tierDiscount.
      discountValue: parseFloat(dataset.discountValue) || 0,
    };
  }

  /**
   * The saving a tier is worth against a given selection, in cents. A fixed
   * amount is the same whatever the bundle costs; the other two depend on what
   * is in it, which is why the discount cannot be resolved once at parse time.
   *
   * @param {?{discountType:string, discountValue:number}} tier
   * @param {Array<{price:number, receivesDiscount:boolean}>} selection
   * @returns {number} integer cents
   */
  static tierDiscount(tier, selection) {
    if (!tier) return 0;
    if (tier.discountType === 'amount') return tier.discountValue;

    const percentOf = (cents) => Math.round((cents * tier.discountValue) / 100);

    if (tier.discountType === 'percent') {
      return percentOf(selection.reduce((sum, item) => sum + item.price, 0));
    }

    // percent_item — "buy one, get 20% off the second".
    //
    // Shopify's Buy X Get Y discounts a single qualifying item, not the pair,
    // so taking the percentage off the subtotal would advertise roughly double
    // what the customer is actually charged. Which item it lands on matters
    // too: a category can be marked as the one that receives the saving (the
    // Pill Compact Pair takes its 20% off the Essentials, not off whichever
    // happens to be cheaper), and where nothing is marked the whole selection
    // is eligible — the AM/PM pair, where both items are the same product.
    //
    // Within whichever pool applies, the cheapest is discounted, mirroring how
    // Shopify chooses between equally qualifying items.
    if (!selection.length) return 0;
    const marked = selection.filter((item) => item.receivesDiscount);
    const pool = marked.length ? marked : selection;
    return percentOf(Math.min(...pool.map((item) => item.price)));
  }

  /**
   * @param {Array<{price:number}>} selection
   * @param {number} discountCents - flat amount earned by the reached tier
   * @returns {{subtotal:number, savings:number, total:number}} integer cents
   */
  static computeTotals(selection, discountCents) {
    const subtotal = selection.reduce((sum, item) => sum + item.price, 0);
    // Clamped both ways: a negative amount in the schema must not inflate the
    // total, and a saving larger than the subtotal must not produce a negative
    // one. Neither is reachable through the tier ladder — a tier only applies
    // once the subtotal has passed its threshold — but the clamp means a
    // mis-typed setting degrades to a sane number rather than a nonsense price.
    const savings = Math.min(Math.max(discountCents, 0), subtotal);
    return { subtotal, savings, total: subtotal - savings };
  }

  /**
   * Collapses repeated variants into quantities, preserving first-appearance order.
   * @param {Array<{variantId:string}>} selection
   * @returns {Array<{id:string, quantity:number}>}
   */
  static collapseDuplicates(selection) {
    const order = [];
    const counts = new Map();
    selection.forEach(({ variantId }) => {
      if (!counts.has(variantId)) {
        counts.set(variantId, 0);
        order.push(variantId);
      }
      counts.set(variantId, counts.get(variantId) + 1);
    });
    return order.map((id) => ({ id, quantity: counts.get(id) }));
  }

  /**
   * @param {Array<{variantId:string}>} selection
   * @param {string} bundleId
   */
  static buildCartPayload(selection, bundleId) {
    return {
      items: CustomBundleBuilder.collapseDuplicates(selection).map(({ id, quantity }) => ({
        id,
        quantity,
        properties: { _bundle_id: bundleId },
      })),
    };
  }

  /**
   * Escapes a value for safe interpolation into HTML text and attribute
   * contexts. Used for every dynamic value written into innerHTML.
   * @param {*} value
   * @returns {string}
   */
  _escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  connectedCallback() {
    this.tierButtons = Array.from(this.querySelectorAll('[data-cbb-tier]'));
    this.slotsEl = this.querySelector('[data-cbb-slots]');
    this.noticeEl = this.querySelector('[data-cbb-notice]');
    this.catalogueEl = this.querySelector('[data-cbb-catalogue]');
    this.summaryEl = this.querySelector('[data-cbb-summary]');
    this.subtotalEl = this.querySelector('[data-cbb-subtotal]');
    this.savingRowEl = this.querySelector('[data-cbb-saving-row]');
    this.savingEl = this.querySelector('[data-cbb-saving]');
    this.totalEl = this.querySelector('[data-cbb-total]');
    this.ctaEl = this.querySelector('[data-cbb-cta]');
    this.ctaLabelEl = this.querySelector('[data-cbb-cta-label]');
    // 'spend' walks the ladder by subtotal, 'quantity' by item count. Every
    // read of "how far along the ladder are we" goes through progressValue, so
    // this is the only place the distinction is decided.
    this.discountMode = this.dataset.discountMode === 'quantity' ? 'quantity' : 'spend';
    // A fixed-size bundle ("pick exactly 2"). Zero leaves it open-ended.
    this.requiredItems = Math.max(0, parseInt(this.dataset.requiredItems, 10) || 0);
    this.errorEl = this.querySelector('[data-cbb-error]');
    this.progressFillEl = this.querySelector('[data-cbb-progress-fill]');
    this.progressLabelEl = this.querySelector('[data-cbb-progress-label]');
    this.moneyFormat = this.dataset.moneyFormat || FoxTheme.settings.moneyFormat;

    this.pills = Array.from(this.querySelectorAll('[data-cbb-pill]'));
    this.rows = Array.from(this.querySelectorAll('[data-cbb-row]'));

    this.trayEl = this.querySelector('[data-cbb-tray]');
    this.trayBodyEl = this.querySelector('[data-cbb-tray-body]');
    this.trayToggleEl = this.querySelector('[data-cbb-tray-toggle]');
    this.trayCountEl = this.querySelector('[data-cbb-tray-count]');
    this.trayTotalEl = this.querySelector('[data-cbb-tray-total]');
    this.cardEl = this.querySelector('[data-cbb-card]');
    this.liveEl = this.querySelector('[data-cbb-live]');

    const plusTemplate = this.querySelector('[data-cbb-icon-plus]');
    const closeTemplate = this.querySelector('[data-cbb-icon-close]');
    this.iconPlus = plusTemplate ? plusTemplate.innerHTML : '+';
    this.iconClose = closeTemplate ? closeTemplate.innerHTML : '&times;';

    // Sorted ascending regardless of the order the blocks were authored in, so
    // earnedTier/nextTier can walk the ladder by position. A merchant who drags
    // the $100 tier above the $50 one changes only how the row looks, never
    // which saving a subtotal earns.
    //
    // A tier with no threshold is dropped rather than kept at zero: every
    // subtotal clears zero, so a blank "Spend at least" field would otherwise
    // publish a rung that reads as permanently unlocked and quotes a saving no
    // discount in admin will honour.
    this.tiers = this.tierButtons
      .map((button, index) => CustomBundleBuilder.parseTier(button, index))
      .filter((tier) => tier.threshold > 0)
      .sort((a, b) => a.threshold - b.threshold);

    this.selection = [];
    // Index of the slot whose entry animation should play on the next render.
    this.enterIndex = null;

    if (this.tiers.length === 0) return;

    this.onTierClick = this.onTierClick.bind(this);
    this.tierButtons.forEach((button) => button.addEventListener('click', this.onTierClick));

    this.onCatalogueClick = this.onCatalogueClick.bind(this);
    this.onSlotsClick = this.onSlotsClick.bind(this);
    this.onCataloguePrefetch = this.onCataloguePrefetch.bind(this);
    this.catalogueEl.addEventListener('click', this.onCatalogueClick);
    // mouseover and focusin are used rather than mouseenter/focus because only
    // these bubble, which delegation from the catalogue root depends on.
    this.catalogueEl.addEventListener('mouseover', this.onCataloguePrefetch);
    this.catalogueEl.addEventListener('focusin', this.onCataloguePrefetch);
    this.slotsEl.addEventListener('click', this.onSlotsClick);

    this.onCtaClick = this.onCtaClick.bind(this);
    this.ctaEl.addEventListener('click', this.onCtaClick);

    this.onPillClick = this.onPillClick.bind(this);
    this.pills.forEach((pill) => pill.addEventListener('click', this.onPillClick));
    this.initScrollSpy();

    if (this.trayToggleEl) {
      this.onTrayToggle = this.onTrayToggle.bind(this);
      this.trayToggleEl.addEventListener('click', this.onTrayToggle);
    }

    this.render();
    this.initTrayObserver();
  }

  disconnectedCallback() {
    if (!this.onTierClick) return;
    this.tierButtons.forEach((button) => button.removeEventListener('click', this.onTierClick));
    this.catalogueEl.removeEventListener('click', this.onCatalogueClick);
    this.catalogueEl.removeEventListener('mouseover', this.onCataloguePrefetch);
    this.catalogueEl.removeEventListener('focusin', this.onCataloguePrefetch);
    this.slotsEl.removeEventListener('click', this.onSlotsClick);
    this.ctaEl.removeEventListener('click', this.onCtaClick);
    this.pills.forEach((pill) => pill.removeEventListener('click', this.onPillClick));
    if (this.rowObserver) this.rowObserver.disconnect();
    if (this.onScrollSpyScroll) {
      window.removeEventListener('scroll', this.onScrollSpyScroll);
      this.onScrollSpyScroll.cancel();
    }
    if (this.trayToggleEl && this.onTrayToggle) {
      this.trayToggleEl.removeEventListener('click', this.onTrayToggle);
    }
    if (this.sectionObserver) this.sectionObserver.disconnect();
    // The tray-visible body class (see _syncTrayHeight) is a global side effect
    // owned by this instance — clear it so a removed element can never leave
    // the page permanently padded at the bottom.
    if (typeof document !== 'undefined' && document.body) document.body.classList.remove('cbb-tray-visible');
  }

  /**
   * The tiers are a ladder, not a selector — the saving is decided by the
   * subtotal, so pressing one cannot change which tier applies. The press is
   * still a useful shortcut to the thing that *does* move the customer up the
   * ladder, so it takes them to the products.
   */
  onTierClick() {
    this.scrollToCatalogue();
  }

  onCatalogueClick(event) {
    const addButton = event.target.closest('[data-cbb-add]');
    if (!addButton) return;
    const card = addButton.closest('[data-cbb-card-product]');
    const item = this.readCardSelection(card);
    if (!item) {
      const message = `${card.dataset.productTitle || 'This product'} is currently unavailable.`;
      this.showNotice(message);
      this.announce(message);
      return;
    }
    this.addItem(item);
  }

  /**
   * Warms the slot thumbnail on hover and on keyboard focus, so by the time a
   * card is actually added the image that lands in the slot is usually already
   * a cache hit and the entry animation has something to fade in.
   */
  onCataloguePrefetch(event) {
    const target = event.target;
    if (!target || !target.closest) return;

    const card = target.closest('[data-cbb-card-product]');
    const source = card && card.dataset.image;
    if (!source || card.dataset.prefetched === 'true') return;

    card.dataset.prefetched = 'true';
    if (typeof Image === 'undefined') return;
    const preload = new Image();
    preload.src = source;
  }

  onSlotsClick(event) {
    const removeButton = event.target.closest('[data-cbb-remove]');
    if (removeButton) {
      this.removeItem(parseInt(removeButton.dataset.cbbRemove, 10));
      return;
    }
    if (event.target.closest('[data-cbb-slot-empty]')) {
      this.scrollToCatalogue();
    }
  }

  onPillClick(event) {
    event.preventDefault();
    const target = document.getElementById(event.currentTarget.dataset.target);
    if (!target) return;
    target.scrollIntoView({ behavior: this.prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
  }

  /**
   * Single source of truth for the reduced-motion preference, so the media
   * query string only lives in one place.
   * @returns {boolean}
   */
  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  initScrollSpy() {
    if (this.rows.length === 0 || typeof IntersectionObserver === 'undefined') return;

    // Tracks which rows are *currently* intersecting the trigger band across
    // callback batches (entries only report what changed this batch, not the
    // full state), so the active pill can be derived deterministically below.
    this.intersectingRows = new Set();

    this.rowObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.intersectingRows.add(entry.target);
          } else {
            this.intersectingRows.delete(entry.target);
          }
        });

        // Several rows can intersect the same batch; entry order isn't
        // contractually document order, so pick the winner explicitly as the
        // first intersecting row in this.rows (already DOM/top-to-bottom
        // order) — the one the reader has actually reached.
        const winner = this.rows.find((row) => this.intersectingRows.has(row));
        if (winner) this.setActivePill(winner.id);
      },
      { rootMargin: '-30% 0px -60% 0px', threshold: 0 }
    );

    this.rows.forEach((row) => this.rowObserver.observe(row));

    // The trigger band only covers the upper-middle of the viewport, so if
    // there isn't enough content below the last row it can never intersect
    // and its pill can never activate. Force it active once the reader hits
    // the bottom of the page, regardless of intersection state — this only
    // *sets* the active pill (same call the observer above makes), so it
    // cooperates with rather than fights that logic; scrolling back up lets
    // the next observer callback take over again naturally.
    this.onScrollSpyScroll = FoxTheme.utils.throttle(() => {
      const scrolledToBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
      if (scrolledToBottom) {
        this.setActivePill(this.rows[this.rows.length - 1].id);
      }
    });
    window.addEventListener('scroll', this.onScrollSpyScroll);
  }

  /**
   * Expands or collapses the tray. Below 750px the tray is a fixed bottom
   * sheet; collapsed it shows the slot strip and the CTA, expanded it adds the
   * price breakdown. There is no cloned copy — these are the real slots, so
   * every remove control works in both states.
   */
  onTrayToggle() {
    if (!this.trayToggleEl) return;
    const open = this.trayToggleEl.getAttribute('aria-expanded') === 'true';
    this.trayToggleEl.setAttribute('aria-expanded', open ? 'false' : 'true');
    this.classList.toggle('cbb__root--tray-open', !open);
    // Refreshes the toggle's accessible name for the new expanded state.
    this.renderTray();

    // The sheet animates to its new height over ~320ms, so offsetHeight read
    // now is the height it is leaving, not arriving at — the body padding
    // compensation would be wrong for the whole transition and then stay wrong.
    // Sync once at the end as well.
    this._syncTrayHeight();
    if (this.trayBodyEl && typeof this.trayBodyEl.addEventListener === 'function') {
      this.trayBodyEl.addEventListener('transitionend', () => this._syncTrayHeight(), { once: true });
    }
  }

  /**
   * The mobile sheet should only be present once the customer is actually in
   * the bundle section — pinning it to the bottom of the viewport while they
   * are still above the section covers unrelated content with a bundle they
   * have not started. Visibility follows the section's own intersection with
   * the viewport.
   *
   * Without IntersectionObserver support the sheet stays visible, which is the
   * safe failure: the desktop layout never renders it as a sheet anyway, and a
   * mobile shopper is better off with a persistent tray than none at all.
   */
  initTrayObserver() {
    if (typeof IntersectionObserver === 'undefined') {
      this._setTrayPresent(true);
      return;
    }

    this.sectionObserver = new IntersectionObserver(
      ([entry]) => this._setTrayPresent(entry.isIntersecting),
      { threshold: 0 }
    );
    this.sectionObserver.observe(this);
  }

  _setTrayPresent(present) {
    if (this.classList) this.classList.toggle('cbb__root--tray-present', present);
    this._syncTrayHeight(present);
  }

  /**
   * Keeps --cbb-tray-height and the body padding-bottom compensation (see
   * section-custom-bundle-builder.css) in sync with the sheet's real rendered
   * height, mirroring the theme's own sticky-atc-bar pattern
   * (assets/section-sticky-atc-bar.css) so page content — the footer, the last
   * product row — is never left stranded underneath it. Guarded on `document`
   * existing so this is safe in a non-browser context.
   * @param {boolean} [present] - omit to keep the current visibility state.
   */
  _syncTrayHeight(present) {
    if (!this.trayEl || typeof document === 'undefined' || !document.body) return;

    const visible = present === undefined
      ? document.body.classList.contains('cbb-tray-visible')
      : present;

    document.body.classList.toggle('cbb-tray-visible', visible);
    if (!visible) return;

    const height = typeof this.trayEl.offsetHeight === 'number' ? this.trayEl.offsetHeight : 0;
    if (document.documentElement && typeof document.documentElement.style.setProperty === 'function') {
      document.documentElement.style.setProperty('--cbb-tray-height', `${height}px`);
    }
  }

  setActivePill(rowId) {
    const reduceMotion = this.prefersReducedMotion();
    this.pills.forEach((pill) => {
      const isActive = pill.dataset.target === rowId;
      pill.setAttribute('aria-current', isActive ? 'true' : 'false');
      if (isActive) {
        pill.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'nearest' });
      }
    });
  }

  /**
   * Each card is a single variant, so there is nothing to disambiguate — the
   * SKU is read straight off the card. Liquid has already skipped unavailable
   * variants, so a card that exists is addable; the null return only covers
   * markup that is malformed or missing.
   * @param {HTMLElement} card
   * @returns {?{variantId:string, productTitle:string, variantTitle:string, price:number, image:string}}
   */
  readCardSelection(card) {
    if (!card || !card.dataset) return null;
    const variantId = card.dataset.variantId;
    const price = parseInt(card.dataset.price, 10);
    if (!variantId || Number.isNaN(price)) return null;
    return {
      variantId,
      productTitle: card.dataset.productTitle || '',
      variantTitle: card.dataset.variantTitle || '',
      price,
      image: card.dataset.image || '',
      category: card.dataset.category || '',
      categoryLabel: card.dataset.categoryLabel || '',
      categoryMax: Math.max(0, parseInt(card.dataset.categoryMax, 10) || 0),
      receivesDiscount: card.dataset.receivesDiscount === 'true',
    };
  }

  /**
   * How an item is named in slot labels and announcements. Products with no
   * real options carry an empty variant title (see the section's
   * has_only_default_variant guard), so the separator is only added when there
   * is actually something to separate.
   * @param {{productTitle:string, variantTitle:string}} item
   * @returns {string}
   */
  itemName(item) {
    if (!item) return '';
    return item.variantTitle ? `${item.productTitle} — ${item.variantTitle}` : item.productTitle;
  }

  /**
   * Announces a message via the polite live region. Blanks the region first
   * and sets the real text on the next frame so screen readers re-announce
   * even when the same message fires twice in a row (e.g. adding the same
   * product back-to-back) — a live region re-assigned an identical string is
   * silently ignored by most screen readers otherwise.
   *
   * Guarded on every side: the live region may not exist (a template variant
   * without it), and requestAnimationFrame may not be available in every
   * environment — degrade to a synchronous set rather than throwing.
   * @param {string} message
   */
  announce(message) {
    if (!this.liveEl) return;
    this.liveEl.textContent = '';
    const raf = typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function'
      ? (cb) => window.requestAnimationFrame(cb)
      : (cb) => cb();
    raf(() => {
      this.liveEl.textContent = message;
    });
  }

  /**
   * Safely queries within the slots list. this.slotsEl is expected to be a
   * full DOM element, but guard rather than assume querySelector is
   * available on it.
   * @param {string} selector
   * @returns {?Element}
   */
  _querySlots(selector) {
    if (!this.slotsEl || typeof this.slotsEl.querySelector !== 'function') return null;
    return this.slotsEl.querySelector(selector);
  }

  /**
   * Moves focus to an element if it exists and actually supports focus(),
   * since not every element reference passed in is guaranteed to be a real,
   * focusable DOM node.
   * @param {?Element} target
   */
  _moveFocus(target) {
    if (target && typeof target.focus === 'function') target.focus();
  }

  /** @returns {number} integer cents */
  get subtotal() {
    return this.selection.reduce((sum, item) => sum + item.price, 0);
  }

  /**
   * What the ladder is measured in: money on a spend ladder, items on a
   * quantity one. Every threshold comparison and every progress figure reads
   * this rather than the subtotal directly.
   * @returns {number}
   */
  get progressValue() {
    return this.discountMode === 'quantity' ? this.selection.length : this.subtotal;
  }

  /**
   * The highest tier the bundle has actually reached, or null while it is
   * still below the first threshold. This — not anything the customer pressed
   * — is what decides the saving shown, so a bundle that grows past a
   * threshold cannot keep displaying the smaller one.
   * @returns {?object}
   */
  get earnedTier() {
    const { progressValue } = this;
    let earned = null;
    for (let i = 0; i < this.tiers.length; i += 1) {
      if (progressValue < this.tiers[i].threshold) break;
      earned = this.tiers[i];
    }
    return earned;
  }

  /**
   * The nearest tier still out of reach — what the progress bar counts toward.
   * Null once the top of the ladder is cleared.
   * @returns {?object}
   */
  get nextTier() {
    const { progressValue } = this;
    return this.tiers.find((tier) => progressValue < tier.threshold) || null;
  }

  /** The saving currently earned, in cents. */
  get earnedDiscount() {
    return CustomBundleBuilder.tierDiscount(this.earnedTier, this.selection);
  }

  /**
   * A fixed-size bundle is capped at exactly that size; an open one at the
   * safety ceiling.
   * @returns {number}
   */
  get maxItems() {
    return this.requiredItems > 0 ? this.requiredItems : CustomBundleBuilder.MAX_ITEMS;
  }

  get isFull() {
    return this.selection.length >= this.maxItems;
  }

  /** How many slots a fixed bundle still has empty. Zero when open-ended. */
  get remainingRequired() {
    return this.requiredItems > 0 ? Math.max(0, this.requiredItems - this.selection.length) : 0;
  }

  /**
   * A fixed-size bundle is only a bundle once it is complete — a half-filled
   * pair does not earn the pair's discount, so letting it reach the cart would
   * quote a saving Shopify will not apply. An open bundle of one is a valid
   * cart, so anything non-empty can submit.
   */
  get canSubmit() {
    if (this.requiredItems > 0) return this.selection.length === this.requiredItems;
    return this.selection.length > 0;
  }

  /**
   * Clears space for an incoming item, and says what that cost.
   *
   * A capped category is a choice, not a queue: the Pill Compact Pair takes one
   * compact of each kind, so pressing a second 7-Day card means "this one
   * instead", never "no". Same for a full fixed-size bundle — a pair whose two
   * slots are taken must still let a customer change their mind on a card
   * rather than making them find the remove control first. Only the open-ended
   * builder's safety ceiling actually refuses, because there nothing about the
   * design says the bundle was meant to stop.
   *
   * @param {object} item
   * @returns {false|null|object} false if the add is blocked, otherwise the
   *   item that was displaced to make room, or null if none was needed.
   */
  _makeRoomFor(item) {
    let displaced = null;

    if (item.categoryMax > 0 && item.category) {
      const sameCategory = this.selection
        .map((chosen, index) => (chosen.category === item.category ? index : -1))
        .filter((index) => index !== -1);
      if (sameCategory.length >= item.categoryMax) {
        [displaced] = this.selection.splice(sameCategory[0], 1);
      }
    }

    if (!this.isFull) return displaced;

    if (this.requiredItems > 0) {
      const [oldest] = this.selection.splice(0, 1);
      return displaced || oldest;
    }

    const message = `You can add up to ${this.maxItems} items to one bundle.`;
    this.showNotice(message);
    this.announce(message);
    return false;
  }

  addItem(item) {
    const displaced = this._makeRoomFor(item);
    if (displaced === false) return false;

    // Captured before the push so crossing a threshold can be detected and
    // called out. Reaching a new saving is the whole point of the ladder, and
    // it is the one change a screen reader user would otherwise have to infer
    // from the progress figure quietly resetting to a larger number.
    const tierBefore = this.earnedTier;
    this.selection.push(item);
    this.clearNotice();
    // renderSlots rebuilds the whole list, so freshly created elements cannot
    // transition from a previous state. Flagging just the slot that was filled
    // lets its entry animation run once, while every other slot re-renders
    // silently instead of the entire tray flashing on each add.
    this.enterIndex = this.selection.length - 1;
    this.render();

    const tierAfter = this.earnedTier;
    const unlocked =
      tierAfter && tierAfter !== tierBefore
        ? `${tierAfter.label} unlocked, saving ${this.savingWords(tierAfter)}. `
        : '';
    const action = displaced
      ? `Replaced ${this.itemName(displaced)} with ${this.itemName(item)}`
      : `Added ${this.itemName(item)}`;
    this.announce(`${action}. ${unlocked}${this.statusSentence()}`);
    return true;
  }

  removeItem(index) {
    if (!Number.isInteger(index) || index < 0 || index >= this.selection.length) return;
    const [removed] = this.selection.splice(index, 1);
    this.clearNotice();
    this.render();
    if (removed) {
      this.announce(`Removed ${this.itemName(removed)}. ${this.statusSentence()}`);
    }
    // The slot that shifted into the removed position, or — if the last item
    // went — the trailing add slot that now sits there.
    const nextFocus =
      this._querySlots(
        `[data-cbb-slot="${index}"] [data-cbb-remove], [data-cbb-slot="${index}"] [data-cbb-slot-empty]`
      ) || this.ctaEl;
    this._moveFocus(nextFocus);
  }

  showNotice(message) {
    this.noticeEl.textContent = message;
    this.noticeEl.hidden = false;
  }

  clearNotice() {
    this.noticeEl.textContent = '';
    this.noticeEl.hidden = true;
  }

  money(cents) {
    return FoxTheme.Currency.formatMoney(cents, this.moneyFormat);
  }

  /**
   * What a tier is worth, in words. A percentage is left as a percentage
   * rather than resolved against the current subtotal: the ladder advertises
   * the offer, and "save 20%" on a rung the customer has not reached yet must
   * not read as a cash figure derived from a bundle they have not built.
   * @param {?object} tier
   * @returns {string}
   */
  savingWords(tier) {
    if (!tier) return '';
    // Both percentage types quote the rate, not a cash figure — what a
    // percent_item tier is worth depends on which item it lands on, and a rung
    // the customer has not reached yet has no item to land on.
    return tier.discountType === 'amount' ? this.money(tier.discountValue) : `${tier.discountValue}%`;
  }

  /**
   * How far a tier still is, in whichever unit the ladder is measured in.
   * @param {?object} tier
   * @returns {string}
   */
  deltaWords(tier) {
    if (!tier) return '';
    const remaining = Math.max(0, tier.threshold - this.progressValue);
    if (this.discountMode !== 'quantity') return this.money(remaining);
    return `${remaining} more item${remaining === 1 ? '' : 's'}`;
  }

  /**
   * Where the bundle stands, in one sentence: size, value, and what the next
   * tier would be worth. Appended to add/remove announcements so a screen
   * reader user hears the same progress a sighted user reads off the bar,
   * which is otherwise silent (it is aria-hidden — the visible label carries
   * the same text without duplicating it into the live region).
   * @returns {string}
   */
  statusSentence() {
    const count = this.selection.length;
    const size = `${count} item${count === 1 ? '' : 's'}, ${this.money(this.subtotal)}.`;
    return `${size} ${this.progressState().message}`.trim();
  }

  /**
   * Where a milestone sits along the rail, as a fraction of the rail's width.
   * The dots are centred in equal columns and the rail spans the whole row, so
   * tier i sits at the midpoint of its own column.
   *
   * @param {number} index
   * @returns {number} 0-1
   */
  tierAnchor(index) {
    return (index + 0.5) / this.tiers.length;
  }

  /**
   * How far along the rail the fill sits, as a percentage.
   *
   * Each tier owns an equal stretch of the rail rather than a slice
   * proportional to its threshold, and the fill interpolates across whichever
   * stretch the subtotal currently falls in. Two things follow, both of which
   * the layout depends on: the milestones can be evenly spaced — a merchant
   * setting 50/75/100 does not get two dots crowded into the last quarter —
   * and the fill arrives exactly on a dot at the moment that tier is cleared,
   * because clearing tier i leaves the subtotal at the start of stretch i+1.
   *
   * Clearing the last tier runs the fill on to the end of the rail rather than
   * stopping at the final dot, so finishing the ladder finishes the bar.
   *
   * @returns {number} 0-100
   */
  ladderPosition() {
    const count = this.tiers.length;
    if (count === 0) return 0;

    const { progressValue } = this;
    for (let i = 0; i < count; i += 1) {
      const { threshold } = this.tiers[i];
      if (progressValue < threshold) {
        // Thresholds are ascending, so the previous one is where this stretch
        // starts; the first stretch starts at zero, from the very start of the
        // rail rather than from a dot.
        const start = i === 0 ? 0 : this.tiers[i - 1].threshold;
        const span = threshold - start;
        const travelled = span > 0 ? Math.min(Math.max((progressValue - start) / span, 0), 1) : 0;
        const from = i === 0 ? 0 : this.tierAnchor(i - 1);
        const to = this.tierAnchor(i);
        return (from + (to - from) * travelled) * 100;
      }
    }
    return 100;
  }

  /**
   * What the rail is counting toward, in words.
   * @returns {{message:string, complete:boolean}}
   */
  progressState() {
    const next = this.nextTier;
    if (!next) {
      return { message: this.dataset.progressMax || '', complete: true };
    }

    const message = (this.dataset.progressNext || '')
      .replace('[amount]', this.deltaWords(next))
      .replace('[saving]', this.savingWords(next));

    return { message, complete: false };
  }

  /**
   * The same sentence with its two figures emphasised. The merchant's template
   * is escaped before anything is substituted into it, and the amounts are
   * escaped as they are wrapped, so no part of this can carry markup out of a
   * theme setting and into the page.
   * @returns {string} HTML
   */
  progressMessageHtml() {
    const { message } = this.progressState();
    if (!message) return '';

    const next = this.nextTier;
    if (!next) return this._escapeHtml(message);

    const amount = this._escapeHtml(this.deltaWords(next));
    const saving = this._escapeHtml(this.savingWords(next));

    // Escaping first leaves the literal [amount] / [saving] markers untouched —
    // neither contains a character the escaper rewrites.
    return this._escapeHtml(this.dataset.progressNext || '')
      .replace('[amount]', `<strong class="cbb__progress-amount">${amount}</strong>`)
      .replace('[saving]', `<strong class="cbb__progress-amount">${saving}</strong>`);
  }

  render() {
    if (!this.tiers || this.tiers.length === 0) return;
    this.renderSlots();
    this.renderTiers();
    this.renderProgress();
    this.renderSummary();
    this.renderCta();
    this.renderTray();
  }

  /**
   * @param {number} index
   * @param {string} label
   * @returns {string} HTML
   */
  _emptySlotHtml(index, label) {
    return `<li class="cbb__slot" data-cbb-slot="${index}">
        <button type="button" class="cbb__slot-plus" data-cbb-slot-empty aria-label="${this._escapeHtml(label)}">${this.iconPlus}</button>
      </li>`;
  }

  /**
   * One slot per chosen item. A fixed-size bundle then shows every slot it
   * still owes, so a pair looks like a pair from the first render and the
   * customer can see what "done" is; an open-ended one shows a single trailing
   * slot inviting the next add, dropped at the ceiling where there is nothing
   * left to invite.
   */
  renderSlots() {
    const filled = this.selection.map((item, i) => {
      const image = this._escapeHtml(item.image);
      const name = this._escapeHtml(this.itemName(item));
      const entering = i === this.enterIndex ? ' cbb__slot--enter' : '';
      return `<li class="cbb__slot cbb__slot--filled${entering}" data-cbb-slot="${i}" aria-label="Item ${i + 1}, ${name}">
        <img class="cbb__slot-img" src="${image}" alt="">
        <button type="button" class="cbb__slot-remove" data-cbb-remove="${i}" aria-label="Remove ${name}">${this.iconClose}</button>
      </li>`;
    });

    if (this.requiredItems > 0) {
      for (let i = this.selection.length; i < this.requiredItems; i += 1) {
        filled.push(this._emptySlotHtml(i, `Choose product ${i + 1} of ${this.requiredItems}`));
      }
    } else if (!this.isFull) {
      const index = this.selection.length;
      filled.push(
        this._emptySlotHtml(index, index === 0 ? 'Choose your first product' : 'Add another product')
      );
    }

    this.slotsEl.innerHTML = filled.join('');

    this._holdEnteringImage();

    // One-shot: the flag is consumed by the render it was set for, so a later
    // unrelated render (a removal, a price update) can't replay the animation
    // on a slot the customer filled a while ago.
    this.enterIndex = null;
  }

  /**
   * If the newly filled slot's photo has not downloaded yet, the entry
   * animation would play over an empty frame and the image would then pop in
   * afterwards. Holding it at zero opacity until `load` fires means the fade
   * always lands on the real photo. Images are usually already cached from the
   * hover prefetch, in which case `complete` is true and nothing is held.
   */
  _holdEnteringImage() {
    if (this.enterIndex === null || typeof this.slotsEl.querySelector !== 'function') return;

    const image = this.slotsEl.querySelector('.cbb__slot--enter .cbb__slot-img');
    if (!image || image.complete) return;

    image.classList.add('cbb__slot-img--pending');
    const settle = () => image.classList.remove('cbb__slot-img--pending');
    image.addEventListener('load', settle, { once: true });
    image.addEventListener('error', settle, { once: true });
  }

  /**
   * Marks every tier the subtotal has cleared, and the highest of them as the
   * one currently in force. Nothing here is a selection — the customer cannot
   * press a tier into or out of this state, only spend into it.
   */
  renderTiers() {
    const earned = this.earnedTier;
    this.tiers.forEach((tier) => {
      const isEarned = earned !== null && tier.threshold <= earned.threshold;
      const isCurrent = earned !== null && tier === earned;
      tier.button.classList.toggle('cbb__tier--earned', isEarned);
      tier.button.classList.toggle('cbb__tier--current', isCurrent);

      const stateEl = tier.button.querySelector('[data-cbb-tier-state]');
      if (stateEl) stateEl.textContent = isEarned ? ' (unlocked)' : ' (not yet reached)';
    });
  }

  renderProgress() {
    if (this.progressFillEl && this.progressFillEl.style) {
      this.progressFillEl.style.width = `${this.ladderPosition()}%`;
    }
    if (this.progressLabelEl) {
      this.progressLabelEl.innerHTML = this.progressMessageHtml();
      // Toggled rather than set, so the celebratory animation on the completed
      // state only replays when the state is actually re-entered.
      this.progressLabelEl.classList.toggle('cbb__progress-label--complete', this.nextTier === null);
    }
  }

  renderSummary() {
    const { subtotal, savings, total } = CustomBundleBuilder.computeTotals(this.selection, this.earnedDiscount);

    this.summaryEl.hidden = this.selection.length === 0;
    this.subtotalEl.textContent = this.money(subtotal);
    this.totalEl.textContent = this.money(total);

    // The row's label is static in the markup — which tier produced this
    // saving is already shown by the filled rung above and named in the
    // announcement when it is crossed, so repeating it here reads as noise.
    const showSaving = savings > 0;
    this.savingRowEl.hidden = !showSaving;
    if (showSaving) this.savingEl.textContent = `−${this.money(savings)}`;
  }

  renderCta() {
    // Writes to the label span so the submit spinner beside it survives; falls
    // back to the button itself if a template variant omits the span.
    const target = this.ctaLabelEl || this.ctaEl;
    if (this.canSubmit) {
      const { total } = CustomBundleBuilder.computeTotals(this.selection, this.earnedDiscount);
      target.textContent = `${this.dataset.ctaReady} · ${this.money(total)}`;
      this.ctaEl.setAttribute('aria-disabled', 'false');
      return;
    }

    this.ctaEl.setAttribute('aria-disabled', 'true');

    // A started-but-unfinished pair is its own state: "choose your first
    // product" is no longer true, and the ready label would promise a checkout
    // the incomplete bundle cannot earn the advertised saving at.
    const remaining = this.remainingRequired;
    if (remaining > 0 && this.selection.length > 0) {
      target.textContent = (this.dataset.ctaIncomplete || 'Choose [count] more').replace(
        '[count]',
        String(remaining)
      );
      return;
    }

    target.textContent = this.dataset.ctaEmpty;
  }

  /**
   * The tray's collapsed summary. On mobile this is the only bundle status
   * visible while browsing, so it carries both the count and the running
   * total; on desktop it is hidden and the full breakdown below it shows
   * instead.
   */
  renderTray() {
    if (!this.trayEl) return;
    const { total } = CustomBundleBuilder.computeTotals(this.selection, this.earnedDiscount);
    const count = this.selection.length;
    const countLabel = `${count} item${count === 1 ? '' : 's'}`;

    if (this.trayCountEl) this.trayCountEl.textContent = countLabel;
    if (this.trayToggleEl) {
      const expanded = this.trayToggleEl.getAttribute('aria-expanded') === 'true';
      this.trayToggleEl.setAttribute(
        'aria-label',
        `Your bundle, ${countLabel}. ${expanded ? 'Hide' : 'Show'} details.`
      );
    }
    if (this.trayTotalEl) {
      this.trayTotalEl.textContent = count > 0 ? this.money(total) : '';
    }
    this._syncTrayHeight();
  }

  onCtaClick() {
    if (!this.canSubmit) {
      this.scrollToCatalogue();
      return;
    }
    this.submit();
  }

  scrollToCatalogue() {
    const firstRow = this.querySelector('[data-cbb-row]');
    if (firstRow) {
      firstRow.scrollIntoView({ behavior: this.prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
    }
  }

  async submit() {
    if (!this.canSubmit || this.isSubmitting) return;

    this.isSubmitting = true;
    this.clearError();
    this.ctaEl.setAttribute('aria-disabled', 'true');
    this.ctaEl.setAttribute('aria-busy', 'true');
    if (this.ctaEl.classList) this.ctaEl.classList.add('cbb__cta--busy');

    const bundleId = `bundle-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const payload = CustomBundleBuilder.buildCartPayload(this.selection, bundleId);

    try {
      // Phase 1 — the add itself. Failure here means nothing happened yet,
      // so it is safe to show an error and leave the selection untouched
      // for the customer to retry.
      let parsed;
      try {
        const response = await fetch(FoxTheme.routes.cart_add_url, {
          ...FoxTheme.utils.fetchConfig('javascript'),
          body: JSON.stringify(payload),
        });
        parsed = await response.json();
      } catch (error) {
        console.error(error);
        this.showError(FoxTheme.cartStrings.error);
        return;
      }

      if (parsed.status) {
        this.showError(parsed.description || parsed.message || FoxTheme.cartStrings.error);
        return;
      }

      // Phase 2 — post-success reconciliation. The add already succeeded on
      // Shopify's server, so the submission is irreversible from here on.
      // Clear the selection and re-render immediately so the UI can never
      // invite a duplicate resubmission, regardless of what happens next.
      this.selection = [];
      this.render();

      // One add_to_cart_clean for the bundle, with the quantities the customer chose (the add.js response
      // carries line totals, not the amounts added).
      if (window.CartAnalytics && Array.isArray(parsed.items)) {
        const requested = new Map(payload.items.map((item) => [String(item.id), item.quantity]));
        window.CartAnalytics.addToCart(
          parsed.items.map((line) => ({
            ...window.CartAnalytics.itemFromCartJson(line),
            quantity: requested.get(String(line.variant_id)) || 1,
          }))
        );
      }

      // Open the drawer on the add, not on the round trip after it. The items
      // are already in the cart at this point, so making the customer wait for
      // a second request before anything visibly happens is pure dead time —
      // it was the bulk of the delay between clicking and seeing the cart.
      document.dispatchEvent(
        new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } })
      );

      // Authoritative cart state for anything subscribed to cartUpdate (badge
      // counts, drawer totals). Deliberately not awaited: nothing the customer
      // is looking at depends on it, and the drawer refreshes itself.
      fetch(FoxTheme.routes.cart_url, { ...FoxTheme.utils.fetchConfig('json', 'GET') })
        .then((response) => response.json())
        .then((cartJson) => {
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
        })
        .catch((error) => {
          // The bundle is already in the cart; a failure here is not a
          // submission failure, so it must not surface as one nor restore the
          // selection.
          console.error(error);
        });
    } finally {
      this.isSubmitting = false;
      this.ctaEl.removeAttribute('aria-busy');
      if (this.ctaEl.classList) this.ctaEl.classList.remove('cbb__cta--busy');
      this.renderCta();
    }
  }

  showError(message) {
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  clearError() {
    this.errorEl.textContent = '';
    this.errorEl.hidden = true;
  }
}

customElements.define('custom-bundle-builder', CustomBundleBuilder);

// Custom elements upgrade automatically on insertion, so a section re-render
// in the theme editor already re-runs connectedCallback in the normal case.
// This only covers the edge case where the element survived in place but its
// internal state was never (re)built — guarded on !el.tiers so a normally-
// upgraded element is never initialised a second time, which would double-bind
// every event listener.
//
// Guarded on `document` existing at all: this runs at script-evaluation
// time (not lazily inside a method like every other document reference in
// this file), so environments without a DOM must not throw here.
if (typeof document !== 'undefined') {
  document.addEventListener('shopify:section:load', (event) => {
    const el = event.target.querySelector('custom-bundle-builder');
    if (el && typeof el.connectedCallback === 'function' && !el.tiers) {
      el.connectedCallback();
    }
  });
}
